import Box from '@mui/material/Box';
import { RHFDateRangePickerField } from '@openx/components-core/lib/DateRangePickerField/RHFDateRangePickerField';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import {
  type DealInsightsDealNameOrIdOptions,
  type DealInsightsMoreFiltersOptions,
  dealInsightsFilterOptionKeys,
} from '@openx/types/dealInsights';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDealContext } from '../../../utils/context/DealContext';
import { filtersToApiParams } from '../../../utils/dealInsightsApiUtils';
import { dealInsightsDateRangeFields, dealInsightsStateFields } from '../../constants';
import { useDealInsightsContext } from '../../context/DealInsightsContext';
import { useDateRangeChipOptions } from '../../hooks/useDateRangeChipOptions';
import { useDateRangeValidationSchema } from '../../hooks/useDateRangeValidationSchema';
import type { DealInsightsState } from '../../types';
import { getDealInsightsSupportedDateRange } from '../../utils/getDealInsightsSupportedDateRange';
import { DealInsightsTabs } from './components/DealInsightsTabs';
import { DealInsightsMoreFilters } from './components/DealInsigthsMoreFilters';
import { DealNameOrIdPopover } from './components/DealNameOrIdPopover';

export const DealInsightsHeader = () => {
  const { filters, dateRange, setDateRange } = useDealInsightsContext();
  const { useDealInsightsFilterOptions } = useDealContext();
  const { minDate, maxDate } = getDealInsightsSupportedDateRange();
  const dateRangeChipOptions = useDateRangeChipOptions(maxDate);
  const dateRangeValidationSchema = useDateRangeValidationSchema(maxDate);

  const { useAccountUid } = useTargetingContext();
  const accountUid = useAccountUid();

  const formMethods = useForm<DealInsightsState>({
    defaultValues: {
      filters,
      dateRange,
      lastOpenedFilterKey: null,
    },
  });

  const observableFormFilters = formMethods.watch(dealInsightsStateFields.FILTERS);

  const { data: filterOptions, loading: filterOptionsLoading } = useDealInsightsFilterOptions(
    filtersToApiParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate ?? maxDate,
      account: accountUid,
      ...observableFormFilters,
    })
  );

  const observableStartDate = formMethods.watch(
    `${dealInsightsStateFields.DATE_RANGE}.${dealInsightsDateRangeFields.START_DATE}`
  );
  const observableEndDate = formMethods.watch(
    `${dealInsightsStateFields.DATE_RANGE}.${dealInsightsDateRangeFields.END_DATE}`
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: This is listener to trigger data api call when filters are updated
  useEffect(() => {
    //Call data api here
  }, [filters, dateRange]);

  useEffect(() => {
    setDateRange({
      startDate: observableStartDate,
      endDate: observableEndDate,
    });
  }, [observableStartDate, observableEndDate, setDateRange]);

  const moreFiltersOptions: DealInsightsMoreFiltersOptions = useMemo(() => {
    return Object.keys(filterOptions).reduce((acc, key) => {
      if (key !== dealInsightsFilterOptionKeys.DEAL) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {} as DealInsightsMoreFiltersOptions);
  }, [filterOptions]);

  const dealNameOrIdOptions: DealInsightsDealNameOrIdOptions = useMemo(() => {
    return {
      [dealInsightsFilterOptionKeys.DEAL]: filterOptions.deal,
    };
  }, [filterOptions]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        backgroundColor: theme => theme.palette.background.boxDark,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        paddingRight: 4,
      }}
      data-test="deal-insights-header"
    >
      <DealInsightsTabs />

      <Box>
        <FormProvider {...formMethods}>
          <form>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <RHFDateRangePickerField
                startDateFormField={`${dealInsightsStateFields.DATE_RANGE}.${dealInsightsDateRangeFields.START_DATE}`}
                endDateFormField={`${dealInsightsStateFields.DATE_RANGE}.${dealInsightsDateRangeFields.END_DATE}`}
                data-test="deal-insights-date-range-picker"
                chipOptions={dateRangeChipOptions}
                minDate={minDate}
                maxDate={maxDate}
                endDateOptional
                validationSchema={dateRangeValidationSchema}
              />
              <DealNameOrIdPopover filterOptions={dealNameOrIdOptions} loading={filterOptionsLoading} />
              <DealInsightsMoreFilters filterOptions={moreFiltersOptions} loading={filterOptionsLoading} />
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};
